import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Dialog } from '@mui/material';
// import { Close } from '@mui/icons-material';
// import { callTypes } from '../../assets/data';



const normalizeOptions = (list = []) =>
    (Array.isArray(list) ? list : [])
        .sort((a, b) => (a?.SortOrder ?? 0) - (b?.SortOrder ?? 0))
        .map((item) => {
            const value =
                item?.Value ??
                item?.value ??
                item?.Name ??
                item?.name;

            const label =
                item?.Label ??
                item?.label ??
                item?.Name ??
                item?.name ??
                value;

            return {
                value,
                label,
            };
        });

const SOURCES = ['online_form', 'email', 'snail_mail', 'teleministry', 'other', 'WePray', 'prayer_center'];

const PrayerRequestDialog = ({ 
  open, 
  onClose, 
  title, 
  request, 
  onChange, 
  onSubmit,
  isNew = false,
  sources = SOURCES 
}) => {
  const characterCount = request?.prayer_request?.length || 0;
    const isNearLimit = characterCount > 450;

    const [categoryOptions, setCategoryOptions] = useState([]);
    const [sourceOptions, setSourceOptions] = useState([]);

    useEffect(() => {

        const fetchDropdownData = async () => {
            try {
                const apiUrl =
                    process.env.REACT_APP_API_URL + "/master-data/prayer-request-fields";

                const response = await fetch(apiUrl, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: process.env.REACT_APP_API_AUTHORIZATION,
                        Accept: "application/json",
                    },
                });

                const data = await response.json();

                if (!response.ok) {
                    throw new Error(data.message || "Failed to fetch dropdown data");
                }

                const sourceOptions = normalizeOptions(data?.data?.Source);
                const categoryOptions = normalizeOptions(data?.data?.Category);

                setSourceOptions(sourceOptions);
                setCategoryOptions(categoryOptions);

            } catch (err) {
                console.error(err);

                const fallback = [{ value: "", label: "---No Records---" }];
                setSourceOptions(fallback);
                setCategoryOptions(fallback);
            }
        };

        fetchDropdownData();
    }, [])  ;

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <StyledDialogHeader>
        <h2>{title}</h2>
        <CloseButton onClick={onClose}>×</CloseButton>
      </StyledDialogHeader>

      <StyledDialogContent>
        <InputGroup>
          <div>
            <label>Requester's Name *</label>
            <Input 
              value={request?.requestedBy || ''} 
              onChange={(e) => onChange({
                ...request,
                requestedBy: e.target.value
              })}
              placeholder="Enter requester's name"
              required
              maxLength={255}
            />
          </div>
        </InputGroup>

        <InputGroup>
          <div>
            <label>Email</label>
            <Input 
              type="email"
              value={request?.email || ''} 
              onChange={(e) => onChange({
                ...request,
                email: e.target.value
              })}
              placeholder="Enter email address"
              maxLength={255}
            />
          </div>
        </InputGroup>

        <InputGroup>
          <div>
            <label>Source *</label>
            <Select
              value={request?.source || ''}
              onChange={(e) => onChange({
                ...request,
                source: e.target.value
              })}
              required
            >
                <option value="">Select source</option>

                {sourceOptions.map((item) => (
                    <option key={item.value} value={item.value}>
                        {item.label}
                    </option>
                ))}
            </Select>
          </div>
        </InputGroup>

        <InputGroup>
          <div>
            <label>Category *</label>
            <Select
              value={request?.category || ''}
              onChange={(e) => onChange({
                ...request,
                category: e.target.value
              })}
              required
            >
              <option value="">Select category</option>
              {categoryOptions.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label>Target Prayer Count *</label>
            <Input 
              type="number"
              value={request?.targetPrayerCount || 1} 
              onChange={(e) => onChange({
                ...request,
                targetPrayerCount: Math.max(1, parseInt(e.target.value) || 1)
              })}
              min="1"
              required
            />
          </div>
        </InputGroup>

        <InputGroup>
          <div>
            <label>Prayer Request *</label>
            <StyledTextArea
              value={request?.prayer_request || ''} 
              onChange={(e) => onChange({
                ...request,
                prayer_request: e.target.value
              })}
              placeholder="Enter prayer request details"
              required
              maxLength={500}
            />
            <CharacterCount $isNearLimit={isNearLimit}>
              {characterCount}/500 characters
            </CharacterCount>
          </div>
        </InputGroup>

        <InputGroup>
          <div>
            <label>Additional Notes</label>
            <TextArea 
              value={request?.notes || ''} 
              onChange={(e) => onChange({
                ...request,
                notes: e.target.value
              })}
              placeholder="Enter any additional notes"
            />
          </div>
        </InputGroup>
      </StyledDialogContent>

      <DialogFooter>
        <RequestId>REQUEST ID: {isNew ? 'NEW' : request?.id}</RequestId>
        <ButtonGroup>
          <Button variant="text" onClick={onClose}>
            CANCEL
          </Button>
          <Button variant="primary" onClick={onSubmit}>
            SAVE
          </Button>
        </ButtonGroup>
      </DialogFooter>
    </Dialog>
  );
};

const StyledDialogHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid #E5E7EB;

  h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
    margin: 0;
  }
`;

const CloseButton = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  color: #666666;
  padding: 4px;

  &:hover {
    color: #000000;
  }
`;

const StyledDialogContent = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  label {
    display: block;
    margin-bottom: 8px;
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
  }
`;

const InputGroup = styled.div`
  display: flex;
  gap: 16px;
  
  > * {
    flex: 1;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #E5E7EB;
  border-radius: 6px;
  font-size: 0.875rem;
  
  &:focus {
    outline: none;
    border-color: #2DB3C0;
  }

  &::placeholder {
    color: #9CA3AF;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #E5E7EB;
  border-radius: 6px;
  font-size: 0.875rem;
  min-height: 100px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #2DB3C0;
  }

  &::placeholder {
    color: #9CA3AF;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #E5E7EB;
  border-radius: 6px;
  font-size: 0.875rem;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #2DB3C0;
  }
`;

const DialogFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-top: 1px solid #E5E7EB;
`;

const RequestId = styled.span`
  color: #666666;
  font-size: 0.875rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
`;

const Button = styled.button`
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  ${props => props.variant === 'primary' && `
    background: #2DB3C0;
    color: white;
    border: none;

    &:hover {
      background: #259AA5;
    }
  `}

  ${props => props.variant === 'text' && `
    background: none;
    color: #666666;
    border: none;

    &:hover {
      background: #F3F4F6;
    }
  `}
`;

const StyledTextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 12px;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  font-size: 14px;
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #2DB3C0;
  }
`;

const CharacterCount = styled.div`
  text-align: right;
  font-size: 12px;
  color: ${props => props.$isNearLimit ? '#FF7234' : '#666666'};
  margin-top: 4px;
`;

export default PrayerRequestDialog; 